import React, { useState, useCallback } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import * as Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import {
  useBulkOperations,
  BulkUser,
  BulkOperationResult,
} from "../../features/users/hooks/useBulkOperations";
import { trpc } from "../../lib/api/trpc";

// Define CSV row structure
interface CSVRow {
  firstName: string;
  lastName: string;
  email: string;
  [key: string]: string; // Allow additional columns
}

// Define validation result
interface ValidationResult {
  row: CSVRow;
  rowNumber: number;
  isValid: boolean;
  errors: string[];
}

// Wizard steps
const steps = [
  "Upload CSV",
  "Preview & Validate",
  "Import Users",
  "View Results",
];

const BulkImportWizard: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [validationResults, setValidationResults] = useState<
    ValidationResult[]
  >([]);
  const [importResult, setImportResult] =
    useState<BulkOperationResult<BulkUser> | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sendOnboardingEmails, setSendOnboardingEmails] = useState(false);

  const { bulkCreateUsers, bulkSendOnboardingEmails, progress, resetProgress } =
    useBulkOperations();

  // Get existing users for duplicate check
  // @ts-ignore - Type checking for trpc.user property
  const { data: existingUsersData } = trpc.user.getUsers.useQuery({
    page: 0,
    limit: 1000, // Get all users for duplicate check
  });

  // Validate CSV data
  const validateData = useCallback(
    (data: CSVRow[]) => {
      const existingEmails = new Set(
        existingUsersData?.users.map((user: any) => user.email.toLowerCase()) ||
          [],
      );
      const csvEmails = new Set<string>();

      const results: ValidationResult[] = data.map((row, index) => {
        const errors: string[] = [];
        const rowNumber = index + 2; // Account for header row

        // Check required fields
        if (!row.firstName?.trim()) {
          errors.push("First name is required");
        }
        if (!row.lastName?.trim()) {
          errors.push("Last name is required");
        }
        if (!row.email?.trim()) {
          errors.push("Email is required");
        } else {
          // Validate email format
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(row.email)) {
            errors.push("Invalid email format");
          } else {
            const emailLower = row.email.toLowerCase();

            // Check for duplicates within CSV
            if (csvEmails.has(emailLower)) {
              errors.push("Duplicate email within CSV");
            }
            csvEmails.add(emailLower);

            // Check against existing users
            if (existingEmails.has(emailLower)) {
              errors.push("Email already exists in system");
            }
          }
        }

        return {
          row,
          rowNumber,
          isValid: errors.length === 0,
          errors,
        };
      });

      setValidationResults(results);
    },
    [existingUsersData],
  );

  // Parse CSV file
  const parseCSV = useCallback(
    (csvFile: File) => {
      Papa.parse<CSVRow>(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          validateData(results.data);
          setActiveStep(1);
        },
        error: (error) => {
          alert(`Error parsing CSV: ${error.message}`);
        },
      });
    },
    [validateData],
  );

  // Handle file drop
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && droppedFile.type === "text/csv") {
        parseCSV(droppedFile);
      } else {
        alert("Please upload a CSV file");
      }
    },
    [parseCSV],
  );

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      parseCSV(selectedFile);
    }
  };

  // Get valid users for import
  const getValidUsers = (): BulkUser[] => {
    return validationResults
      .filter((result) => result.isValid)
      .map((result) => ({
        firstName: result.row.firstName.trim(),
        lastName: result.row.lastName.trim(),
        email: result.row.email.trim(),
      }));
  };

  // Handle import
  const handleImport = async () => {
    setActiveStep(2);
    const validUsers = getValidUsers();

    try {
      const result = await bulkCreateUsers(validUsers);
      setImportResult(result);

      // If sendOnboardingEmails is true and we have successful imports, send the emails
      if (sendOnboardingEmails && result.successful.length > 0) {
        // Get the user IDs from the successful imports
        // Note: This assumes the API returns user objects with IDs
        // We'll need to update this once we know the actual API response structure
        const userIds = result.successful
          .map((user: any) => user.id || user)
          .filter(Boolean);

        if (userIds.length > 0) {
          await bulkSendOnboardingEmails(userIds);
        }
      }

      setActiveStep(3);
    } catch (error) {
      console.error("Import failed:", error);
      alert("Import failed. Please try again.");
      setActiveStep(1);
    }
  };

  // Handle step navigation
  const handleBack = () => {
    if (activeStep === 1) {
      // Reset when going back from preview
      setValidationResults([]);
    }
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setValidationResults([]);
    setImportResult(null);
    resetProgress();
  };

  // Generate sample CSV
  const generateSampleCSV = () => {
    const sampleData = [
      ["firstName", "lastName", "email"],
      ["John", "Doe", "john.doe@example.com"],
      ["Jane", "Smith", "jane.smith@example.com"],
      ["Bob", "Johnson", "bob.johnson@example.com"],
    ];

    const csv = sampleData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_users.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Render content based on active step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Upload Step
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload CSV File
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload a CSV file containing user information. The file must have
              columns for firstName, lastName, and email.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={generateSampleCSV}
              sx={{ mb: 3 }}
            >
              Download Sample CSV
            </Button>

            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragging
                  ? "action.hover"
                  : "background.paper",
                borderStyle: "dashed",
                borderWidth: 2,
              }}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
            >
              <UploadIcon
                sx={{ fontSize: 48, color: "action.disabled", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                Drag and drop your CSV file here
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                or
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleFileSelect}
                />
              </Button>
            </Paper>
          </Box>
        );

      case 1: {
        // Preview Step
        const validCount = validationResults.filter((r) => r.isValid).length;
        const invalidCount = validationResults.filter((r) => !r.isValid).length;

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Preview & Validate
            </Typography>

            <Alert
              severity={invalidCount > 0 ? "warning" : "success"}
              sx={{ mb: 2 }}
            >
              {validCount} valid user{validCount !== 1 ? "s" : ""} ready to
              import
              {invalidCount > 0 &&
                `, ${invalidCount} invalid row${invalidCount !== 1 ? "s" : ""} will be skipped`}
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendOnboardingEmails}
                  onChange={(e) => setSendOnboardingEmails(e.target.checked)}
                  color="primary"
                />
              }
              label="Send onboarding email to imported users"
              sx={{ mb: 2 }}
            />

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResults.map((result, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: result.isValid
                          ? "inherit"
                          : "error.light",
                        opacity: result.isValid ? 1 : 0.7,
                      }}
                    >
                      <TableCell>{result.rowNumber}</TableCell>
                      <TableCell>
                        {result.isValid ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>{result.row.firstName}</TableCell>
                      <TableCell>{result.row.lastName}</TableCell>
                      <TableCell>{result.row.email}</TableCell>
                      <TableCell>
                        {result.errors.map((error, i) => (
                          <Chip
                            key={i}
                            label={error}
                            size="small"
                            color="error"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      }

      case 2: // Import Step
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Importing Users...
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {progress.message}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 2, mb: 2 }}
            />
            <Typography variant="body2">
              {progress.current} of {progress.total} users processed
            </Typography>
          </Box>
        );

      case 3: // Results Step
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Import Complete
            </Typography>

            {importResult && (
              <>
                <Alert
                  severity={
                    importResult.failed.length === 0 ? "success" : "warning"
                  }
                  sx={{ mb: 3 }}
                >
                  Successfully imported {importResult.successful.length} user
                  {importResult.successful.length !== 1 ? "s" : ""}
                  {importResult.failed.length > 0 &&
                    `, ${importResult.failed.length} failed`}
                  {sendOnboardingEmails &&
                    importResult.successful.length > 0 &&
                    ". Onboarding emails have been sent."}
                </Alert>

                {importResult.failed.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Failed Imports:
                    </Typography>
                    <List dense>
                      {importResult.failed.map((failure, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`${failure.item.firstName} ${failure.item.lastName} (${failure.item.email})`}
                            secondary={failure.error}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/users")}
                  >
                    View All Users
                  </Button>
                  <Button variant="outlined" onClick={handleReset}>
                    Import More Users
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 400 }}>{renderStepContent()}</Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button
          disabled={activeStep === 0 || activeStep === 2}
          onClick={handleBack}
        >
          Back
        </Button>

        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={getValidUsers().length === 0}
          >
            Import {getValidUsers().length} Valid User
            {getValidUsers().length !== 1 ? "s" : ""}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default BulkImportWizard;
